/**
 * Componente Global de Detalle - Tipo 2
 *
 * Componente reutilizable para todas las ventanas de detalle/vista del sistema.
 * Basado en el diseño final aprobado de ProductDetailModal.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { styled, SxProps, Theme, alpha } from '@mui/material/styles';
import { colors } from '../../../styles/theme/colors';
import {
  DialogZoomTransition,
  DIALOG_ZOOM_MS,
  dialogZoomSlotProps,
  dialogZoomDialogSx,
} from '../DialogZoomTransition';
import { useTabScopedOpen } from '../../dashboard/TabPanelActiveContext';

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  ...dialogZoomDialogSx,
  '& .MuiDialog-container': {
    ...dialogZoomDialogSx['& .MuiDialog-container'],
    height: '100%',
  },
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    pointerEvents: 'auto',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
  color: '#ffffff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontWeight: 600,
  fontSize: '1.25rem', // Aumentado de h6 (1.25rem) a un poco más grande
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: '#ffffff',
  '&:hover': {
    color: alpha(theme.palette.common.white, 0.85),
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  flex: 1,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.default : colors.grey[50],
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
}));

const CloseButtonStyled = styled(Button)(({ theme }) => ({
  minWidth: '120px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : colors.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.12) : colors.grey[100],
    color: theme.palette.text.primary,
  },
}));

export interface DetailModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** Se fusiona con los estilos base del Paper (p. ej. maxWidth custom con maxWidth={false}). */
  paperSx?: SxProps<Theme>;
  showCloseButton?: boolean;
  closeButtonText?: string;
  footerActions?: React.ReactNode; // Botones personalizados para el footer
  className?: string;
}

/**
 * Modal de detalle unificado para todo el sistema
 *
 * Diseño final aprobado:
 * - Header: Gradiente verde-azul
 * - Animación: Zoom suave (350ms)
 * - Botón cerrar: Estilo gris Tipo 1
 * - Tamaño: Configurable (default: md)
 */
const DetailModal: React.FC<DetailModalProps> = ({
  open,
  onClose,
  title,
  icon,
  children,
  maxWidth = 'md',
  paperSx,
  showCloseButton = true,
  closeButtonText = 'Cerrar',
  footerActions,
  className,
}) => {
  const scopedOpen = useTabScopedOpen(open);

  return (
    <StyledDialog
      open={scopedOpen}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      className={className}
      TransitionComponent={DialogZoomTransition}
      transitionDuration={DIALOG_ZOOM_MS}
      scroll="paper"
      slotProps={dialogZoomSlotProps}
      PaperProps={{
        sx: [
          {
            maxHeight: '90vh',
            margin: 'auto',
            my: 'auto',
            pointerEvents: 'auto',
          },
          ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : []),
        ],
      }}
    >
      {/* Header with gradient background */}
      <StyledDialogTitle>
        <TitleContainer>
          {icon && icon}
          {title}
        </TitleContainer>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <ContentContainer>{children}</ContentContainer>
      </StyledDialogContent>

      {/* Footer with action buttons */}
      {footerActions ? (
        <StyledDialogActions>{footerActions}</StyledDialogActions>
      ) : showCloseButton ? (
        <StyledDialogActions>
          <CloseButtonStyled onClick={onClose}>{closeButtonText}</CloseButtonStyled>
        </StyledDialogActions>
      ) : null}
    </StyledDialog>
  );
};

export default DetailModal;
